import React, { useState } from 'react';
import { AccountTier, VaultRole, VaultStatus, type VaultResponse } from '../types/api';
import { apiPost, apiDownload } from '../api/client';
import type { CustomerPortalResponse } from '../types/api';
import SubscribeModal from './SubscribeModal';
import './TrialBanner.css';

interface Props {
  vault: VaultResponse;
  userRole: VaultRole;
}

function getDaysRemaining(trialEndsAt: string): number {
  const utc = trialEndsAt.endsWith('Z') ? trialEndsAt : trialEndsAt + 'Z';
  const end = new Date(utc).getTime();
  const now = Date.now();
  return Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
}

export default function TrialBanner({ vault, userRole }: Props) {
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Only the owner sees billing banners
  if (userRole !== VaultRole.Owner) return null;

  // Paid+Active vaults: no banner needed — Manage Billing lives in vault-actions row
  if (vault.accountTier === AccountTier.Paid && vault.status === VaultStatus.Active) return null;

  async function handleDownload() {
    setExportLoading(true);
    try {
      const { blob, fileName } = await apiDownload(`/api/vault/${vault.id}/export`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // silently fail
    } finally {
      setExportLoading(false);
    }
  }

  async function handleOpenPortal() {
    setPortalLoading(true);
    try {
      const data = await apiPost<CustomerPortalResponse>(`/api/vault/${vault.id}/billing/portal`, {});
      window.location.href = data.portalUrl;
    } catch {
      setPortalLoading(false);
    }
  }

  // Read-only: trial expired or subscription lapsed
  if (vault.status === VaultStatus.ReadOnly) {
    return (
      <>
        <div className="trial-banner trial-banner--readonly">
          <div className="trial-banner-content">
            <span className="trial-banner-icon">🔒</span>
            <div>
              <strong>Your vault is read-only.</strong>
              {' '}You can view and download everything, but no new changes can be saved.
              Subscribe to restore full access.
            </div>
          </div>
          <div className="trial-banner-actions">
            <button
              className="trial-banner-download-btn"
              onClick={handleDownload}
              disabled={exportLoading}
            >
              {exportLoading ? 'Preparing…' : '⬇ Export Vault (ZIP)'}
            </button>
            <button className="btn-primary trial-banner-btn" onClick={() => setShowSubscribeModal(true)}>
              Subscribe Now
            </button>
          </div>
        </div>
        {showSubscribeModal && (
          <SubscribeModal vaultId={vault.id} onClose={() => setShowSubscribeModal(false)} />
        )}
      </>
    );
  }

  // Past due: payment failed, still has access
  if (vault.status === VaultStatus.PastDue) {
    return (
      <div className="trial-banner trial-banner--pastdue">
        <div className="trial-banner-content">
          <span className="trial-banner-icon">⚠️</span>
          <div>
            <strong>Payment failed.</strong>
            {' '}Your vault still has full access during the grace period.
            Please update your payment method to avoid interruption.
          </div>
        </div>
        <button className="btn-primary trial-banner-btn" onClick={handleOpenPortal} disabled={portalLoading}>
          {portalLoading ? 'Opening…' : 'Update Payment'}
        </button>
      </div>
    );
  }

  // Trial active
  if (vault.accountTier === AccountTier.Trial && vault.trialEndsAt) {
    const days = getDaysRemaining(vault.trialEndsAt);
    const urgent = days <= 3;

    return (
      <>
        <div className={`trial-banner trial-banner--trial${urgent ? ' trial-banner--urgent' : ''}`}>
          <div className="trial-banner-content">
            <span className="trial-banner-icon">⏳</span>
            <div>
              {days === 0
                ? <><strong>Your free trial ends today.</strong> Subscribe now to keep full access.</>
                : <><strong>{days} day{days !== 1 ? 's' : ''} left in your free trial.</strong> Subscribe to keep your vault active after the trial ends.</>
              }
            </div>
          </div>
          <button className="btn-primary trial-banner-btn" onClick={() => setShowSubscribeModal(true)}>
            Subscribe
          </button>
        </div>
        {showSubscribeModal && (
          <SubscribeModal vaultId={vault.id} onClose={() => setShowSubscribeModal(false)} />
        )}
      </>
    );
  }

  return null;
}
